import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
    AlertTriangle, Boxes, CheckCircle2, CreditCard, Loader2, Lock, Package, RefreshCw, Store, Truck,
} from "lucide-react";
import BackOffice from "@/components/account/BackOffice";
import { Btn, Chip, PageHead, Section, Toggle, cellCls } from "@/components/account/Shell";
import api, { formatApiError } from "@/lib/api";

const money = (n) => `$${Number(n || 0).toFixed(2)}`;
const PAYOUT = { paid: ["green", "Paid to you"], waiting_connect: ["amber", "Connect Stripe"], failed: ["red", "Held"] };

const STEPS = [
    ["You flip one switch.", "Every product 13thav stocks copies onto your shelves. They are switched on, live and priced."],
    ["Those shelves lock.", "The names, the photos and the base prices are 13thav's. You have nothing to write, shoot or keep up with."],
    ["You set your percent over.", "You can set it per shelf. Whatever you add on top of our price is 100% yours, on top of your 20%."],
    ["A customer buys in your shop.", "They design it and check out with you, at your price. Your name is the one on the order."],
    ["13thav prints it, packs it and ships it.", "The order lands on our floor in Brooklyn. We make it, box it and mail it. You do nothing."],
    ["Your money moves on its own.", "Your 20% lands in your own Stripe account the moment the card clears. There is no invoicing and no chasing."],
    ["Any shelf you would rather print yourself stays yours.", "Set a shelf to \"I print this\" and it unlocks and comes back to you."],
];

function ShelfRow({ room, rate, maxMarkup, busy, run }) {
    const [pct, setPct] = useState(String(room.markup_pct ?? 0));
    useEffect(() => setPct(String(room.markup_pct ?? 0)), [room.markup_pct]);
    const dirty = String(room.markup_pct) !== String(Number(pct) || 0);

    return (
        <div className={`flex flex-wrap items-center gap-x-3 gap-y-1.5 border-t border-[#d0d0d0] px-2.5 py-1.5 first:border-t-0 ${room.consigned ? "border-l-4 border-l-[#b42318]" : ""}`}
            data-testid={`consign-room-${room.key}`}>
            <span className="min-w-[9rem] flex-1 text-sm font-semibold text-[#1a1a1a]">{room.label}</span>
            <span className="text-[11px] font-semibold uppercase tabular-nums text-[#4a4a4a]">
                {room.live}/{room.products} live
            </span>
            {room.consigned ? (
                <Chip tone="red" testid={`consign-room-state-${room.key}`}><Lock size={9} /> 13thav prints this · locked</Chip>
            ) : (
                <Chip tone="green" testid={`consign-room-state-${room.key}`}><Store size={9} /> I print this</Chip>
            )}

            {room.consigned && (
                <span className="flex items-center gap-1.5">
                    <span className="text-[11px] font-semibold uppercase text-[#4a4a4a]">percent over</span>
                    <input type="number" min="0" max={maxMarkup} step="0.5" value={pct}
                        onChange={(e) => setPct(e.target.value)} data-testid={`consign-room-markup-${room.key}`}
                        className={`${cellCls} w-14`} />
                    <Btn tone="solid" disabled={busy || !dirty} testid={`consign-room-markup-save-${room.key}`}
                        onClick={() => run(() => api.put("/store/consignment/room-markup",
                            { room: room.key, markup_pct: Math.min(Math.max(Number(pct) || 0, 0), maxMarkup) }))}>
                        Save
                    </Btn>
                    {room.override ? (
                        <button onClick={() => run(() => api.put("/store/consignment/room-markup", { room: room.key, markup_pct: null }))}
                            disabled={busy} data-testid={`consign-room-default-${room.key}`}
                            className="text-[12px] font-semibold text-[#1a56db] hover:underline">use default</button>
                    ) : (
                        <span className="text-[11px] uppercase text-[#4a4a4a]">default</span>
                    )}
                    <span className="text-[12px] font-semibold tabular-nums text-[#1a1a1a]">
                        keeps {money(15 * rate + 15 * (Number(pct) || 0) / 100)} of a $15 job
                    </span>
                </span>
            )}

            <span className="ml-auto flex items-center gap-2">
                <span className="text-[11px] font-semibold uppercase text-[#4a4a4a]">Who prints it</span>
                <Toggle on={room.private} disabled={busy} testid={`consign-private-${room.key}`}
                    labelOn="I print this" labelOff="13thav prints this"
                    onChange={(v) => run(() => api.put("/store/consignment/rooms", { room: room.key, private: v }))} />
            </span>
        </div>
    );
}

export default function ConsignmentMode() {
    const [data, setData] = useState(null);
    const [busy, setBusy] = useState(false);
    const [markup, setMarkup] = useState("0");
    const [confirm, setConfirm] = useState(false);

    const load = useCallback(() => {
        api.get("/store/consignment").then(({ data }) => {
            setData(data);
            setMarkup(String(data.state?.markup_pct ?? 0));
        }).catch((e) => setData({ error: formatApiError(e.response?.data?.detail) }));
    }, []);
    useEffect(() => { load(); }, [load]);

    const run = async (fn) => {
        setBusy(true);
        try {
            const { data: res } = await fn();
            setData(res);
            if (res.state) setMarkup(String(res.state.markup_pct ?? 0));
            if (res.note) toast.success(res.note);
            setConfirm(false);
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
        finally { setBusy(false); }
    };

    if (!data) return <BackOffice><div className="flex min-h-[40vh] items-center justify-center">
        <Loader2 className="animate-spin text-[#4a4a4a]" /></div></BackOffice>;

    if (data.error || !data.eligible) return (
        <BackOffice>
            <div className="mx-auto max-w-2xl py-16 text-center" data-testid="consign-locked">
                <Boxes size={32} className="mx-auto text-[#4a4a4a]" />
                <h1 className="mt-3 text-xl font-bold text-[#1a1a1a]">Consignment is a Pro Shop thing.</h1>
                <p className="mt-2 text-sm text-[#4a4a4a]">
                    {data.error || "13th Ave itself is the shop being consigned from, so it never sees this switch. Move up to a Pro Shop or a Virtual Brick & Mortar and we print and ship everything for you."}
                </p>
                <Link to="/account/sell" className="mt-5 inline-block">
                    <Btn tone="solid" testid="consign-open-space">See the spaces</Btn></Link>
            </div>
        </BackOffice>
    );

    const st = data.state;
    const yours = Math.round(st.rate * 100);
    const on = st.enabled;
    const example = 100 * (1 + (Number(markup) || 0) / 100);
    const mine = 100 * st.rate + (example - 100);

    return (
        <BackOffice>
            <PageHead kicker="Shop settings" title="Consignment mode" testid="consign-head"
                sub="Pick what you print yourself. For everything else, 13thav prints it, packs it and ships it under your name, and you keep 20% of the sale plus any percent you add on top."
                right={
                    <div className="text-right">
                        <p className="text-[11px] font-semibold uppercase tracking-[0.06em] text-[#4a4a4a]">Your cut</p>
                        <p className="text-2xl font-bold tabular-nums text-[#1a1a1a]" data-testid="consign-rate">{yours}%</p>
                        <p className="text-[11px] uppercase text-[#4a4a4a]">plus your percent over</p>
                    </div>
                } />

            <div className={`mt-3 ${on ? "border-2 border-[#b42318]" : ""}`}
                data-testid={on ? "consign-on-frame" : undefined}>
                <Section testid="consign-master"
                    title={on ? "Consignment is ON. The shelves marked \"13thav prints this\" are locked." : "Consignment is OFF. You print and ship everything yourself."}
                    note={on ? `All ${data.master_products} of 13thav's products are on your shelves. Pick which shelves you print yourself below.`
                        : "One switch puts 13thav's whole catalog on your shelves. You then pick which shelves you print yourself."}
                    right={
                        on ? (
                            confirm ? (
                                <span className="flex flex-wrap items-center gap-1.5">
                                    <span className="text-[12px] font-semibold text-[#b42318]">
                                        Your shelves unlock and every order comes back to you to print and ship.
                                    </span>
                                    <Btn tone="danger" disabled={busy} testid="consign-off-yes"
                                        onClick={() => run(() => api.post("/store/consignment", { enabled: false }))}>
                                        Yes, turn it off
                                    </Btn>
                                    <Btn testid="consign-off-no" onClick={() => setConfirm(false)}>Keep it on</Btn>
                                </span>
                            ) : (
                                <Btn testid="consign-enable-off" onClick={() => setConfirm(true)}>Turn consignment off</Btn>
                            )
                        ) : (
                            <Btn tone="solid" disabled={busy} testid="consign-enable"
                                onClick={() => run(() => api.post("/store/consignment", { enabled: true }))}>
                                Turn consignment on
                            </Btn>
                        )
                    }>
                    <div className="space-y-3">
                        <div>
                            <p className="flex items-start gap-2 border border-[#1a1a1a] bg-[#1a1a1a] p-2.5 text-sm font-semibold text-white bo-inv"
                                data-testid="consign-promise">
                                <Truck size={15} className="mt-0.5 shrink-0" />
                                {data.promise} You do not need a printer, film, boxes or a trip to the post office. You sell it, and 13thav makes it and mails it.
                            </p>

                            <h3 className="mt-3 text-sm font-semibold text-[#1a1a1a]">How this works, step by step</h3>
                            <ol className="mt-1.5 divide-y divide-[#d0d0d0] border-y border-[#d0d0d0]"
                                data-testid="consign-steps">
                                {STEPS.map(([head, body], i) => (
                                    <li key={head} className="flex gap-2.5 py-1.5">
                                        <span className="mt-px flex h-[18px] w-[18px] shrink-0 items-center justify-center border border-[#1a1a1a] bg-[#1a1a1a] text-[11px] font-bold text-white bo-inv">
                                            {i + 1}
                                        </span>
                                        <span className="min-w-0">
                                            <span className="text-[13px] font-semibold text-[#1a1a1a]">{head}. </span>
                                            <span className="text-[13px] text-[#4a4a4a]">{body}</span>
                                        </span>
                                    </li>
                                ))}
                            </ol>

                            <dl className="mt-3 divide-y divide-[#d0d0d0] border-b border-[#d0d0d0]">
                                {[
                                    ["You keep", `${yours}% of 13th Ave's price on every single sale`],
                                    ["We keep", `${100 - yours}% — and we do the printing, the packing and the shipping`],
                                    ["Your percent over", `Up to ${data.max_markup}% on top, per shelf. 100% of that is yours.`],
                                    ["What's locked", "Names, photos, base prices, listing and deleting on any consigned shelf"],
                                    ["What's still yours", "Your percent over, your counters, your coupons, your billboards, your store page"],
                                    ["The split", `Locked at ${yours}/${100 - yours}. Only 13thav support can move it.`],
                                ].map(([k, v]) => (
                                    <div key={k} className="flex flex-wrap items-baseline justify-between gap-3 py-1.5">
                                        <dt className="text-[11px] font-semibold uppercase tracking-[0.06em] text-[#4a4a4a]">{k}</dt>
                                        <dd className="text-right text-[13px] font-semibold text-[#1a1a1a]">{v}</dd>
                                    </div>
                                ))}
                            </dl>
                        </div>

                        <aside className="border border-[#d0d0d0] bg-[#fafafa] p-2.5" data-testid="consign-example">
                            <p className="text-[11px] font-semibold uppercase tracking-[0.06em] text-[#4a4a4a]">
                                On a $100 job
                            </p>
                            <div className="mt-1.5 max-w-xl space-y-1 text-[13px] text-[#1a1a1a]">
                                <p className="flex justify-between"><span>The customer pays you</span>
                                    <span className="font-bold tabular-nums">{money(example)}</span></p>
                                <p className="flex justify-between"><span>Your {yours}% commission</span>
                                    <span className="tabular-nums">{money(100 * st.rate)}</span></p>
                                <p className="flex justify-between"><span>Your percent over</span>
                                    <span className="tabular-nums">{money(example - 100)}</span></p>
                                <p className="flex justify-between border-t border-[#d0d0d0] pt-1 font-semibold">
                                    <span>You keep</span><span className="font-bold tabular-nums">{money(mine)}</span></p>
                                <p className="flex justify-between text-[#4a4a4a]"><span>13th Ave keeps</span>
                                    <span className="tabular-nums">{money(example - mine)}</span></p>
                                <p className="border-t border-[#d0d0d0] pt-1 text-[12px] text-[#4a4a4a]">
                                    …and out of their share they buy the blank, print it, pack it and pay the shipping.
                                </p>
                            </div>
                        </aside>
                    </div>
                </Section>
            </div>

            {on && (
                <>
                    <div className="mt-2">
                        <Section testid="consign-markup" title="Your default percent over"
                            note={`0 – ${data.max_markup}% over 13th Ave's price on every shelf that doesn't set its own`}>
                            <div className="flex flex-wrap items-end gap-2.5">
                                <label className="flex items-end gap-1.5">
                                    <span className="pb-1 text-[11px] font-semibold uppercase text-[#4a4a4a]">Percent over</span>
                                    <input type="number" min="0" max={data.max_markup} step="0.5" value={markup}
                                        onChange={(e) => setMarkup(e.target.value)} data-testid="consign-markup-input"
                                        className={`${cellCls} w-16`} />
                                    <span className="pb-1 text-sm font-semibold text-[#1a1a1a]">%</span>
                                </label>
                                <Btn tone="solid" disabled={busy} testid="consign-markup-save"
                                    onClick={() => run(() => api.put("/store/consignment/markup", { markup_pct: Number(markup) || 0 }))}>
                                    Save default
                                </Btn>
                                <p className="pb-1 text-[13px] text-[#1a1a1a]">
                                    A $15 tee sells for{" "}
                                    <span className="font-bold tabular-nums">${(15 * (1 + (Number(markup) || 0) / 100)).toFixed(2)}</span>{" "}
                                    in your shop — you keep{" "}
                                    <span className="font-bold tabular-nums">{money(15 * st.rate + 15 * (Number(markup) || 0) / 100)}</span>.
                                </p>
                            </div>
                        </Section>
                    </div>

                    <div className="mt-2">
                        <Section pad={false} testid="consign-rooms" title="Shelf by shelf"
                            note="Set one shelf's percent over, or take a shelf back and print it yourself">
                            {data.rooms.map((r) => (
                                <ShelfRow key={r.key} room={r} rate={st.rate} maxMarkup={data.max_markup}
                                    busy={busy} run={run} />
                            ))}
                            <p className="flex items-start gap-2 border-t border-[#d0d0d0] bg-[#fafafa] px-2.5 py-2 text-[13px] text-[#1a1a1a]">
                                <Truck size={13} className="mt-0.5 shrink-0" />
                                Every shelf marked <span className="font-semibold">we print it</span> is ours to make and
                                mail — and locked in your back office. Anything you flip to{" "}
                                <span className="font-semibold">mine</span> unlocks, comes back to your bench, and the
                                money for it goes straight to your own register.
                            </p>
                        </Section>
                    </div>

                    <div className="mt-2">
                        <Section testid="consign-money" title="Getting paid"
                            note="Your cut moves to your Stripe the moment the card clears — you never invoice us"
                            right={<span className="text-[12px] font-semibold tabular-nums text-[#1a1a1a]">
                                {money(data.earned)} paid · {money(data.waiting)} waiting
                            </span>}>
                            {data.stripe?.charges_enabled ? (
                                <p className="flex items-center gap-2 text-sm text-[#1a1a1a]" data-testid="consign-stripe-ok">
                                    <CheckCircle2 size={15} className="text-[#067647]" /> Your Stripe account is connected — transfers land automatically.
                                </p>
                            ) : (
                                <div className="flex flex-wrap items-center gap-2.5 border border-[#b54708] bg-[#fffaeb] p-2.5"
                                    data-testid="consign-stripe-missing">
                                    <AlertTriangle size={15} className="text-[#b54708]" />
                                    <p className="min-w-[15rem] flex-1 text-sm text-[#1a1a1a]">
                                        Connect Stripe or we can't send your cut. We hold every cent of it until you do.
                                    </p>
                                    <Link to="/account/payouts"><Btn tone="solid" testid="consign-connect-stripe">
                                        <CreditCard size={11} /> Connect Stripe
                                    </Btn></Link>
                                </div>
                            )}

                            <div className="mt-2.5 border border-[#d0d0d0]" data-testid="consign-payouts">
                                {(data.payouts || []).length ? data.payouts.map((p) => {
                                    const [tone, label] = PAYOUT[p.status] || PAYOUT.failed;
                                    return (
                                        <div key={p.id} className="flex flex-wrap items-center gap-x-3 gap-y-1 border-t border-[#d0d0d0] px-2.5 py-1.5 text-sm first:border-t-0"
                                            data-testid={`consign-payout-${p.id}`}>
                                            <span className="text-[12px] font-bold text-[#1a1a1a]">{p.order_number}</span>
                                            <span className="text-[13px] text-[#4a4a4a]">{p.lines} line{p.lines === 1 ? "" : "s"}</span>
                                            <Chip tone={tone}>{label}</Chip>
                                            {p.reason && <span className="text-[12px] text-[#4a4a4a]">{p.reason}</span>}
                                            <span className="ml-auto text-sm font-bold tabular-nums text-[#1a1a1a]">{money(p.amount)}</span>
                                            {p.status !== "paid" && (
                                                <Btn disabled={busy} testid={`consign-retry-${p.id}`}
                                                    onClick={() => run(() => api.post(`/store/consignment/payouts/${p.id}/retry`))}>
                                                    <RefreshCw size={11} /> Retry
                                                </Btn>
                                            )}
                                        </div>
                                    );
                                }) : (
                                    <p className="px-2.5 py-5 text-center text-sm text-[#4a4a4a]" data-testid="consign-payouts-empty">
                                        No consignment sales yet. The first one shows up here the second the card clears.
                                    </p>
                                )}
                            </div>
                        </Section>
                    </div>

                    <p className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 border border-[#d0d0d0] bg-white px-2.5 py-2 text-[13px] text-[#1a1a1a]">
                        <Package size={14} />
                        <span>Consigned orders still land in your{" "}
                            <Link to="/account/orders" className="font-semibold text-[#1a56db] hover:underline">order inbox</Link>{" "}
                            marked <span className="font-semibold">consignment</span> — you can watch every one of them
                            move, but you never have to touch one.</span>
                        <Link to="/account/products" data-testid="consign-see-shelves"
                            className="ml-auto text-[12px] font-semibold text-[#1a56db] hover:underline">
                            <Store size={11} className="mr-1 inline" /> see my locked shelves →
                        </Link>
                    </p>
                </>
            )}
        </BackOffice>
    );
}
